import { getApiErrorMessage } from '../utils';
import { Modal, Button } from '@majidalfuttaim/maf-ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cancelDgcOrder } from 'api/requests/dgc/dgcOrders';
import { CircleCrossIcon } from 'assets/svg';
import { type PropsWithChildren } from 'react';
import Spinner2 from 'shared/components/Spinner2';
import { toast } from 'shared/hooks/useToast';
import { queryKeyBuilder } from 'shared/query';

type CancelOrderModalProps = PropsWithChildren & {
  orderUuid: string;
  orderRef: string | null;
  open: boolean;
  onOpenChange: (open: boolean, reset?: boolean) => void;
};

function CancelOrderModal({ orderUuid, orderRef, open, onOpenChange, children }: CancelOrderModalProps) {
  const queryClient = useQueryClient();

  const cancelMutation = useMutation({
    mutationFn: () => cancelDgcOrder(orderUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dgcOrders'] });
      queryClient.invalidateQueries({ queryKey: queryKeyBuilder.dgcOrderDetail(orderUuid) });
      onOpenChange(false, true);
    },
    onError: (error) => {
      toast({ title: getApiErrorMessage(error), variant: 'error' });
      // The row may now be stale (e.g. someone else already cancelled it) — refresh it regardless of the outcome.
      queryClient.invalidateQueries({ queryKey: ['dgcOrders'] });
      onOpenChange(false, true);
    },
  });

  return (
    <Modal.Root open={open} onOpenChange={onOpenChange}>
      {children && <Modal.Trigger asChild>{children}</Modal.Trigger>}
      <Modal.Content className="!bg-surface-50 !max-w-[384px]">
        <Modal.Header className="pt-9">
          <Modal.Icon>
            <CircleCrossIcon className="text-[#D92D20] mb-2.5" />
          </Modal.Icon>
          <Modal.Title className="font-klarheit text-2xl text-center">Cancel this order?</Modal.Title>
          <Modal.Description className="text-black text-base font-inter text-center max-w-md">
            {orderRef ? `Order ${orderRef} will be cancelled. ` : 'This order will be cancelled. '}
            This cannot be undone.
          </Modal.Description>
        </Modal.Header>
        <Modal.Footer className="flex-wrap">
          <Button className="w-full" rounded={true} disabled={cancelMutation.isPending} onClick={cancelMutation.mutate}>
            <div className="flex gap-2 items-center">
              {cancelMutation.isPending && <Spinner2 className="text-white w-4 h-4" />}
              <span>Confirm Cancellation</span>
            </div>
          </Button>
          <Button className="w-full" rounded={true} variant="outline" onClick={() => onOpenChange(false)}>
            Keep Order
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}

export default CancelOrderModal;
